import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Search,
    Filter,
    MapPin,
    FileText,
    Edit2,
    Loader2,
    UserCheck,
    Globe,
    Phone,
    Mail,
    User,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { toast } from "../hooks/use-toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { CountryCodeSelect } from "./CountryCodeSelect";

const CustomerManagement = () => {
    const { user } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter and search states
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCustomerType, setSelectedCustomerType] = useState("");

    // Pagination states
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const limit = 10;

    // Expanded address details state (mapped by userId)
    const [expandedAddresses, setExpandedAddresses] = useState({});
    const [loadingAddresses, setLoadingAddresses] = useState({});

    // Edit modal states
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [editName, setEditName] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [editCountryCode, setEditCountryCode] = useState("+91");
    const [editPassport, setEditPassport] = useState("");
    const [editCustomerType, setEditCustomerType] = useState("B2C Guest");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadCustomers();
    }, [page]);

    // Reset page to 1 on search/filter changes
    useEffect(() => {
        if (page !== 1) {
            setPage(1);
        } else {
            loadCustomers();
        }
    }, [searchQuery, selectedCustomerType]);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const params = {
                page,
                limit
            };
            if (searchQuery.trim()) {
                params.search = searchQuery;
            }
            if (selectedCustomerType) {
                params.customer_type = selectedCustomerType;
            }
            const response = await api.getCustomers(params);
            setCustomers(response.data.items || []);
            setTotal(response.data.total || 0);
            setHasMore(response.data.has_more || false);
        } catch (error) {
            console.error("Failed to load customers:", error);
            toast({
                title: "Failed to fetch customers list",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const toggleAddressDetails = async (userId) => {
        // If already loaded and expanding, toggle visibility
        if (expandedAddresses[userId]) {
            setExpandedAddresses((prev) => ({
                ...prev,
                [userId]: { ...prev[userId], isVisible: !prev[userId].isVisible },
            }));
            return;
        }

        try {
            setLoadingAddresses((prev) => ({ ...prev, [userId]: true }));
            const response = await api.getCustomerAddress(userId);
            const addressData = response.data || {};
            setExpandedAddresses((prev) => ({
                ...prev,
                [userId]: {
                    data: addressData,
                    isVisible: true,
                },
            }));
        } catch (error) {
            console.error("Failed to load customer address:", error);
            toast({
                title: "Failed to fetch address details",
                variant: "destructive",
            });
        } finally {
            setLoadingAddresses((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const openEditModal = (customer) => {
        setEditingCustomer(customer);
        setEditName(customer.name || "");
        setEditPhone(customer.phone || "");
        setEditCountryCode(customer.country_code || "+91");
        setEditPassport(customer.passport_number || "");
        setEditCustomerType(customer.customer_type || "B2C Guest");
        setEditModalOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editName.trim()) {
            toast({ title: "Name is required", variant: "destructive" });
            return;
        }

        try {
            setSaving(true);
            await api.updateProfile(editingCustomer.user_id, {
                name: editName,
                phone: editPhone,
                country_code: editCountryCode,
                passport_number: editPassport || null,
                customer_type: editCustomerType,
            });

            toast({ title: "Customer updated successfully!" });
            setEditModalOpen(false);
            loadCustomers();
        } catch (error) {
            console.error("Failed to update customer:", error);
            toast({
                title: "Failed to update customer details",
                description: error?.response?.data?.detail || "Something went wrong",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        <UserCheck className="w-7 h-7 text-orange-600 animate-pulse" />
                        Customers Directory
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Manage registered client profiles, search by name/email/phone, and view billing details.
                    </p>
                </div>
            </div>

            {/* Filter and search control bar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:max-w-md">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder="Search by name, email, or phone number..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-10 border-gray-200 focus:ring-orange-500 focus:border-orange-500 rounded-lg"
                    />
                </div>

                <div className="flex flex-wrap gap-3 w-full md:w-auto justify-end">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-gray-400" />
                        <select
                            value={selectedCustomerType}
                            onChange={(e) => setSelectedCustomerType(e.target.value)}
                            className="h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">All Customer Types</option>
                            <option value="B2C Guest">B2C Guest</option>
                            <option value="B2B">B2B</option>
                            <option value="Corporate">Corporate</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Customers Listing Table/Grid */}
            {loading ? (
                <div className="flex justify-center items-center py-20 bg-white rounded-xl border">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-10 w-10 animate-spin text-orange-600" />
                        <span className="text-sm text-gray-500">Loading customer profiles...</span>
                    </div>
                </div>
            ) : customers.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border">
                    <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">No Customers Found</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                        No customer profiles matched your current search filters. Try clearing filters or refining your query.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4">
                        {customers.map((customer) => {
                            const hasAddressDetail = expandedAddresses[customer.user_id];
                            const isAddressVisible = hasAddressDetail?.isVisible;
                            const address = hasAddressDetail?.data || {};
                            const isAddrLoading = loadingAddresses[customer.user_id];

                            return (
                                <div
                                    key={customer.user_id}
                                    className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                                >
                                    <div className="p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                        <div className="space-y-1.5 flex-1">
                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                <h3 className="text-base font-semibold text-gray-900">
                                                    {customer.name}
                                                </h3>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-100 uppercase">
                                                    {customer.customer_type || "B2C Guest"}
                                                </span>
                                                {customer.passport_number && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                        Passport: {customer.passport_number}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 pt-1 text-sm text-gray-500">
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                                                    <span>{customer.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                                                    <span>
                                                        {customer.phone ? `${customer.country_code || "+91"} ${customer.phone}` : "No phone provided"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                                                    <span>Registered: {new Date(customer.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center gap-2.5 w-full md:w-auto shrink-0 justify-end border-t md:border-0 pt-3 md:pt-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => toggleAddressDetails(customer.user_id)}
                                                className="text-gray-700 border-gray-200 text-xs flex items-center gap-1.5 h-9"
                                                disabled={isAddrLoading}
                                            >
                                                {isAddrLoading ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                ) : (
                                                    <MapPin className="w-3.5 h-3.5" />
                                                )}
                                                {isAddressVisible ? "Hide Details" : "Show Details"}
                                                {isAddressVisible ? (
                                                    <ChevronUp className="w-3 h-3 ml-0.5" />
                                                ) : (
                                                    <ChevronDown className="w-3 h-3 ml-0.5" />
                                                )}
                                            </Button>

                                            <Button
                                                size="sm"
                                                onClick={() => openEditModal(customer)}
                                                className="bg-orange-600 hover:bg-orange-700 text-white text-xs flex items-center gap-1.5 h-9"
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                                Edit Customer
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Collapsible lazy-loaded address block */}
                                    {isAddressVisible && (
                                        <div className="bg-slate-50 px-5 py-4 border-t border-slate-100 text-sm">
                                            {Object.keys(address).length === 0 ? (
                                                <div className="text-gray-500 py-1 flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-400" />
                                                    <span>No billing address or PAN details found for this customer.</span>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {/* Billing Address column */}
                                                    <div className="space-y-1">
                                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Billing Address</span>
                                                        <div className="text-gray-700 leading-relaxed font-medium">
                                                            <p>{address.address_line1}</p>
                                                            {address.address_line2 && <p>{address.address_line2}</p>}
                                                            {address.landmark && <p className="text-xs text-gray-500">Landmark: {address.landmark}</p>}
                                                            <p>
                                                                {address.city}, {address.state} - {address.pincode}
                                                            </p>
                                                            <p>{address.country}</p>
                                                        </div>
                                                    </div>

                                                    {/* Tax details column */}
                                                    <div className="space-y-2">
                                                        <div>
                                                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">PAN Number</span>
                                                            <span className="text-gray-800 font-semibold bg-white border border-gray-200 px-2.5 py-1 rounded text-xs inline-block mt-1">
                                                                {address.pan || "Not Provided"}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">GSTIN</span>
                                                            <span className="text-gray-800 font-semibold bg-white border border-gray-200 px-2.5 py-1 rounded text-xs inline-block mt-1">
                                                                {address.gstin || "Not Provided"}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Client Name Details */}
                                                    <div className="space-y-1">
                                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Billing Profile Name</span>
                                                        <span className="text-gray-700 block font-medium mt-1">
                                                            {address.name || customer.name}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination Controls */}
                    {total > 0 && (
                        <div className="bg-white px-5 py-4 rounded-xl border flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm mt-5">
                            <div className="text-sm text-gray-500 font-medium text-center sm:text-left">
                                Showing {Math.min(total, (page - 1) * limit + 1)}–{Math.min(total, page * limit)} of {total} customers
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-gray-400">
                                    Page {page} of {Math.ceil(total / limit) || 1}
                                </span>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={page === 1}
                                        className="text-gray-700 h-9 px-3 text-xs font-semibold"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((prev) => prev + 1)}
                                        disabled={!hasMore}
                                        className="text-gray-700 h-9 px-3 text-xs font-semibold"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Customer Dialog */}
            <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Customer Profile</DialogTitle>
                        <DialogDescription>
                            Update customer details including passport number and customer type.
                        </DialogDescription>
                    </DialogHeader>
                    {editingCustomer && (
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div>
                                <Label htmlFor="edit-customer-name">Name *</Label>
                                <Input
                                    id="edit-customer-name"
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    required
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label>Phone Number</Label>
                                <div className="flex gap-2 mt-1">
                                    <CountryCodeSelect
                                        value={editCountryCode}
                                        onChange={setEditCountryCode}
                                    />
                                    <Input
                                        id="edit-customer-phone"
                                        type="tel"
                                        value={editPhone}
                                        onChange={(e) => setEditPhone(e.target.value)}
                                        className="flex-1"
                                        placeholder="1234567890"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="edit-customer-passport">Passport Number</Label>
                                <Input
                                    id="edit-customer-passport"
                                    type="text"
                                    value={editPassport}
                                    onChange={(e) => setEditPassport(e.target.value)}
                                    className="mt-1"
                                    placeholder="Passport Number (Optional)"
                                />
                            </div>

                            <div>
                                <Label htmlFor="edit-customer-type">Customer Type *</Label>
                                <select
                                    id="edit-customer-type"
                                    value={editCustomerType}
                                    onChange={(e) => setEditCustomerType(e.target.value)}
                                    className="w-full h-10 px-3 border border-gray-200 rounded-md bg-white text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                    <option value="B2C Guest">B2C Guest</option>
                                    <option value="B2B">B2B</option>
                                    <option value="Corporate">Corporate</option>
                                </select>
                            </div>

                            <DialogFooter className="pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setEditModalOpen(false)}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="bg-orange-600 text-white hover:bg-orange-700"
                                >
                                    {saving && (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    )}
                                    {saving ? "Saving Changes..." : "Save Changes"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CustomerManagement;
